import { getCellActiveValue } from '../tableDirectEdit/tableDirectEditUseCase';

/** @type {ReadonlySet<string>} */
const FINDABLE_CELL_TYPES = new Set([
  'text',
  'textarea',
  'markdown',
  'enum',
  'color',
  'time',
  'date',
  'datetime',
  'int',
  'amount',
  'percent',
  'list',
]);

/**
 * @typedef {object} TableFindEntry
 * @property {string} groupId
 * @property {number} groupIndex
 * @property {string} rowId
 * @property {number} rowIndexInGroup
 * @property {string} text
 */

/**
 * Lowercase, accent-insensitive form of a string for client-side find.
 * @param {string} text
 * @returns {string}
 */
export function normalizeFindText(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
}

/**
 * Searchable text of one cell (empty when the type is not findable).
 * @param {TableCellValueObjectCustom | null | undefined} cellValue
 * @returns {string}
 */
export function stringifyCellForFind(cellValue) {
  if (!cellValue || !FINDABLE_CELL_TYPES.has(cellValue.type)) return '';
  const activeValue = getCellActiveValue(cellValue);
  if (activeValue == null) return '';
  if (Array.isArray(activeValue)) {
    return activeValue
      .map((item) => (item && typeof item === 'object' ? item.lib : ''))
      .filter(Boolean)
      .join(' ');
  }
  if (typeof activeValue === 'object') return '';
  return String(activeValue);
}

/**
 * Ordered find index for loaded table groups (one entry per row).
 * @param {TableGroupsCustom[]} tableGroups
 * @param {Array<{ id: string }>} columns
 * @returns {TableFindEntry[]}
 */
export function buildTableFindIndex(tableGroups, columns) {
  const columnIds = (columns ?? [])
    .map((column) => column.id)
    .filter((id) => id && id !== 'static-checkbox');

  /** @type {TableFindEntry[]} */
  const entries = [];
  (tableGroups ?? []).forEach((group, groupIndex) => {
    (group.data ?? []).forEach((row, rowIndexInGroup) => {
      const parts = columnIds.map((columnId) => stringifyCellForFind(row.cells?.[columnId]));
      entries.push({
        groupId: group.id,
        groupIndex,
        rowId: row.id,
        rowIndexInGroup,
        text: normalizeFindText(parts.join(' ')),
      });
    });
  });
  return entries;
}

/**
 * Rows whose concatenated text contains the query (one match per row, display order).
 * @param {TableFindEntry[]} index
 * @param {string} query
 * @returns {TableFindEntry[]}
 */
export function findMatchingRows(index, query) {
  const normalizedQuery = normalizeFindText(query);
  if (!normalizedQuery) return [];
  return (index ?? []).filter((entry) => entry.text.includes(normalizedQuery));
}

/**
 * @param {number} currentIndex
 * @param {number} matchCount
 * @param {1 | -1} step
 * @returns {number}
 */
export function cycleFindMatchIndex(currentIndex, matchCount, step) {
  if (matchCount <= 0) return 0;
  return (currentIndex + step + matchCount) % matchCount;
}
